import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, MenuItem } from "@mui/material";
import { LOYALTY_API_BASE } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import BirthdateValueEditor from "./BirthdateValueEditor";
import { BIRTHDATE_FIELD } from "../../utils/birthdateWire";
import { resolveFieldDescriptor, valueInputHelperText } from "../../utils/conditionFieldUtils";
import { valueKindLabel } from "../../utils/fieldTypeRegistry";
import { toDateInputValue } from "../../../segment/utils/unomiConditionUtils";
import {
    TypedChipListField,
    ValidatedBetweenFields,
    ValidatedScalarField,
} from "./TypedValueInputs";

const fsx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const TYPED_SCALAR_KINDS = new Set(["email", "phone", "uri", "uuid", "ipv4", "time", "number"]);
const TYPED_LIST_KINDS = new Set(["email", "phone", "uri", "uuid", "ipv4", "number"]);

const TEXT_OPERATORS_LOYALTY = new Set(["contains"]);
const TEXT_OPERATORS_UNOMI = new Set(["contains", "startsWith", "endsWith"]);

function isSystemObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value) && "$system" in value;
}

function isListOperator(operator) {
    return operator === "in";
}

function isRangeOperator(operator) {
    return operator === "between";
}

function isExistsOperator(operator) {
    return operator === "exists";
}

function isTextOperator(operator, dialect) {
    if (dialect === "unomi") return TEXT_OPERATORS_UNOMI.has(operator);
    return TEXT_OPERATORS_LOYALTY.has(operator);
}

/**
 * Éditeur de valeur — piloté par la combinaison champ + opérateur.
 */
export default function ConditionValueEditor({
    dialect = "loyalty",
    field,
    operator,
    value,
    onChange,
    catalog = null,
    brandName = "",
    accentColor = "#3cb0d6",
    unomiField = null,
}) {
    const descriptor = resolveFieldDescriptor(field, catalog || {}, dialect, unomiField);
    const helper = valueInputHelperText(descriptor, operator, dialect);
    const inputKind = descriptor.inputKind || descriptor.valueKind;
    const itemsKind = descriptor.itemsKind || "string";
    const ui = descriptor.ui || {};
    const enumOptions = ui.options || descriptor.enumOptions || [];

    const [tierOptions, setTierOptions] = useState([]);
    const [rewardOptions, setRewardOptions] = useState([]);
    const [loadingTiers, setLoadingTiers] = useState(false);
    const [loadingRewards, setLoadingRewards] = useState(false);

    const isLoyaltyTier = descriptor.special === "loyalty_tier";
    const isReward = descriptor.special === "reward";

    useEffect(() => {
        if (!isLoyaltyTier || !brandName) {
            setTierOptions([]);
            return;
        }
        setLoadingTiers(true);
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-tiers?brand=${encodeURIComponent(brandName)}&active=true`, {
            headers: { "X-Brand": brandName },
        })
            .then((r) => (r.ok ? r.json() : []))
            .then((items) => setTierOptions(Array.isArray(items) ? items : []))
            .catch(() => setTierOptions([]))
            .finally(() => setLoadingTiers(false));
    }, [isLoyaltyTier, brandName]);

    useEffect(() => {
        if (!isReward || !brandName) {
            setRewardOptions([]);
            return;
        }
        loyaltyFetch(`${LOYALTY_API_BASE}/rewards?brand=${encodeURIComponent(brandName)}`, {
            headers: { "X-Brand": brandName },
        })
            .then((r) => (r.ok ? r.json() : []))
            .then((items) => setRewardOptions(Array.isArray(items) ? items : []))
            .catch(() => setRewardOptions([]))
            .finally(() => setLoadingRewards(false));
    }, [isReward, brandName]);

    if (descriptor.isBirthdate && dialect === "loyalty") {
        return (
            <BirthdateValueEditor
                value={value}
                onChange={onChange}
                operator={operator}
                fieldMeta={catalog?.fieldMeta?.[BIRTHDATE_FIELD]}
                valuePresets={catalog?.valuePresets?.birthdate}
                accentColor={accentColor}
            />
        );
    }

    if (isExistsOperator(operator)) {
        return (
            <TextField
                size="small"
                fullWidth
                select
                label="Existe ?"
                value={value === true || value === "true" ? "true" : "false"}
                onChange={(e) => onChange(e.target.value === "true")}
                helperText={helper}
                sx={fsx}
            >
                <MenuItem value="true">Oui (non nul)</MenuItem>
                <MenuItem value="false">Non (nul)</MenuItem>
            </TextField>
        );
    }

    if (inputKind === "object") {
        return (
            <TextField
                size="small"
                fullWidth
                disabled
                label="Objet"
                value=""
                helperText={helper || "Seul l'opérateur « existe » est supporté pour les objets."}
                sx={fsx}
            />
        );
    }

    if (inputKind === "array") {
        if (isTextOperator(operator, dialect)) {
            return (
                <ValidatedScalarField
                    inputKind={itemsKind}
                    value={value}
                    onChange={onChange}
                    label={`Texte (${valueKindLabel(itemsKind)})`}
                    helperText={helper}
                    ui={ui}
                />
            );
        }
        if (isListOperator(operator) || operator === "eq" || operator === "neq" || operator === "equals" || operator === "notEquals") {
            return (
                <TypedChipListField
                    inputKind={itemsKind}
                    value={Array.isArray(value) ? value : (value != null && value !== "" ? [value] : [])}
                    onChange={onChange}
                    label={`Éléments (${valueKindLabel(itemsKind)})`}
                    helperText={helper}
                />
            );
        }
    }

    if (isLoyaltyTier) {
        const multi = isListOperator(operator);
        const selected = multi
            ? (Array.isArray(value) ? value.map(String) : [value].filter(Boolean).map(String))
            : (value != null && value !== "" ? String(value) : "");
        const byKey = new Map(tierOptions.map((t) => [String(t.key), t.name || String(t.key)]));
        return (
            <TextField
                size="small"
                fullWidth
                select
                label={loadingTiers ? "Chargement des niveaux…" : "Niveau fidélité"}
                InputLabelProps={{ shrink: true }}
                value={selected}
                onChange={(e) => {
                    const next = e.target.value;
                    onChange(multi ? (Array.isArray(next) ? next : [next].filter(Boolean)) : next);
                }}
                helperText={helper || `${tierOptions.length} niveau(x) disponible(s)`}
                sx={fsx}
                SelectProps={{
                    multiple: multi,
                    displayEmpty: true,
                    renderValue: (s) => {
                        if (!multi) return byKey.get(String(s)) || s || "Sélectionnez un niveau";
                        const keys = Array.isArray(s) ? s : [];
                        if (!keys.length) return "Sélectionnez un ou plusieurs niveaux";
                        return keys.map((k) => byKey.get(String(k)) || k).join(", ");
                    },
                }}
            >
                {tierOptions.length === 0 && !loadingTiers && (
                    <MenuItem value="" disabled>Aucun niveau disponible</MenuItem>
                )}
                {tierOptions.map((tier) => (
                    <MenuItem key={tier.id || tier.key} value={String(tier.key)}>
                        {tier.name || tier.key}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    if (isReward) {
        const multi = isListOperator(operator);
        const selected = multi
            ? (Array.isArray(value) ? value.map(String) : [value].filter(Boolean).map(String))
            : (value != null && value !== "" ? String(value) : "");
        const byId = new Map(rewardOptions.map((r) => [String(r.id), r.name || String(r.id)]));
        return (
            <TextField
                size="small"
                fullWidth
                select
                label={loadingRewards ? "Chargement des récompenses…" : "Récompense"}
                InputLabelProps={{ shrink: true }}
                value={selected}
                onChange={(e) => {
                    const next = e.target.value;
                    onChange(multi ? (Array.isArray(next) ? next : [next].filter(Boolean)) : next);
                }}
                helperText={helper}
                sx={fsx}
                SelectProps={{
                    multiple: multi,
                    displayEmpty: true,
                    renderValue: (s) => {
                        if (!multi) return byId.get(String(s)) || s || "Sélectionnez une récompense";
                        const ids = Array.isArray(s) ? s : [];
                        if (!ids.length) return "Sélectionnez une ou plusieurs récompenses";
                        return ids.map((id) => byId.get(String(id)) || id).join(", ");
                    },
                }}
            >
                {rewardOptions.length === 0 && !loadingRewards && (
                    <MenuItem value="" disabled>Aucune récompense disponible</MenuItem>
                )}
                {rewardOptions.map((r) => (
                    <MenuItem key={r.id} value={String(r.id)}>{r.name || r.id}</MenuItem>
                ))}
            </TextField>
        );
    }

    if (isRangeOperator(operator)) {
        const useTypedBetween = TYPED_SCALAR_KINDS.has(inputKind);
        if (useTypedBetween) {
            return (
                <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        <ValidatedBetweenFields
                            inputKind={inputKind}
                            value={value}
                            onChange={onChange}
                            helperText={helper}
                            ui={ui}
                        />
                    </Box>
                </Box>
            );
        }
        const arr = Array.isArray(value) ? value : ["", ""];
        const isNum = inputKind === "number";
        const isDate = inputKind === "date" || inputKind === "datetime";
        const isTime = inputKind === "time";
        return (
            <Box display="flex" flexDirection="column" gap={0.5}>
                <Box display="flex" gap={1} flexWrap="wrap">
                    <TextField
                        size="small"
                        fullWidth
                        type={isTime ? "time" : isDate ? "date" : isNum ? "number" : "text"}
                        label="De"
                        value={isDate ? toDateInputValue(arr[0]) : (arr[0] ?? "")}
                        onChange={(e) => {
                            const v = e.target.value;
                            onChange([isNum && v !== "" ? Number(v) : v, arr[1]]);
                        }}
                        InputLabelProps={(isDate || isTime) ? { shrink: true } : undefined}
                        sx={{ ...fsx, flex: 1, minWidth: 120 }}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        type={isTime ? "time" : isDate ? "date" : isNum ? "number" : "text"}
                        label="À"
                        value={isDate ? toDateInputValue(arr[1]) : (arr[1] ?? "")}
                        onChange={(e) => {
                            const v = e.target.value;
                            onChange([arr[0], isNum && v !== "" ? Number(v) : v]);
                        }}
                        InputLabelProps={(isDate || isTime) ? { shrink: true } : undefined}
                        sx={{ ...fsx, flex: 1, minWidth: 120 }}
                    />
                </Box>
                {helper && <Typography fontSize={11} color="text.secondary">{helper}</Typography>}
            </Box>
        );
    }

    if (isListOperator(operator)) {
        if (enumOptions.length > 0) {
            const selected = Array.isArray(value) ? value.map(String) : [value].filter((v) => v != null && v !== "").map(String);
            return (
                <TextField
                    size="small"
                    fullWidth
                    select
                    label="Valeurs"
                    InputLabelProps={{ shrink: true }}
                    value={selected}
                    onChange={(e) => {
                        const next = e.target.value;
                        onChange(Array.isArray(next) ? next : [next].filter(Boolean));
                    }}
                    helperText={helper || "Sélectionnez une ou plusieurs valeurs"}
                    sx={fsx}
                    SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (s) => {
                            const keys = Array.isArray(s) ? s : [];
                            return keys.length ? keys.join(", ") : "Sélectionnez…";
                        },
                    }}
                >
                    {enumOptions.map((opt) => (
                        <MenuItem key={opt} value={String(opt)}>{opt}</MenuItem>
                    ))}
                </TextField>
            );
        }

        if (TYPED_LIST_KINDS.has(inputKind)) {
            return (
                <TypedChipListField
                    inputKind={inputKind}
                    value={Array.isArray(value) ? value : []}
                    onChange={onChange}
                    label={`Valeurs (${valueKindLabel(inputKind)})`}
                    helperText={helper}
                />
            );
        }

        return (
            <TypedChipListField
                inputKind="string"
                value={Array.isArray(value) ? value : []}
                onChange={onChange}
                label="Valeurs"
                helperText={helper || "Entrée pour ajouter chaque valeur"}
            />
        );
    }

    if (isTextOperator(operator, dialect)) {
        return (
            <TextField
                size="small"
                fullWidth
                label="Texte"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                helperText={helper || "Texte libre"}
                sx={fsx}
            />
        );
    }

    if (inputKind === "boolean") {
        return (
            <TextField
                size="small"
                fullWidth
                select
                label="Valeur"
                value={value === true || value === "true" ? "true" : "false"}
                onChange={(e) => onChange(e.target.value === "true")}
                helperText={helper}
                sx={fsx}
            >
                <MenuItem value="true">{dialect === "unomi" ? "Vrai" : "Oui"}</MenuItem>
                <MenuItem value="false">{dialect === "unomi" ? "Faux" : "Non"}</MenuItem>
            </TextField>
        );
    }

    if (inputKind === "enum" && enumOptions.length > 0) {
        return (
            <TextField
                size="small"
                fullWidth
                select
                label="Valeur"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                helperText={helper}
                sx={fsx}
            >
                {enumOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
            </TextField>
        );
    }

    if (inputKind === "number") {
        return (
            <ValidatedScalarField
                inputKind="number"
                value={value}
                onChange={onChange}
                label="Nombre"
                helperText={helper}
                ui={ui}
            />
        );
    }

    if (inputKind === "date" || inputKind === "datetime") {
        const presets = catalog?.valuePresets?.[inputKind] || catalog?.valuePresets?.[descriptor.valueKind] || [];
        const isSystemObj = isSystemObject(value);
        const rawDisplay = isSystemObj ? JSON.stringify(value) : (value ?? "");
        const matchedPreset = presets.find((p) =>
            isSystemObj ? JSON.stringify(p.value) === JSON.stringify(value) : false,
        );
        const presetSelectVal = matchedPreset ? matchedPreset.id : "__custom__";
        const showManual = presets.length === 0 || presetSelectVal === "__custom__";

        return (
            <Box display="flex" flexDirection="column" gap={1}>
                {presets.length > 0 && dialect === "loyalty" && (
                    <TextField
                        size="small"
                        fullWidth
                        select
                        label={`Preset (${inputKind})`}
                        value={presetSelectVal}
                        onChange={(e) => {
                            if (e.target.value === "__custom__") {
                                onChange("");
                            } else {
                                const preset = presets.find((p) => p.id === e.target.value);
                                if (preset) onChange(preset.value);
                            }
                        }}
                        sx={fsx}
                    >
                        {presets.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                        ))}
                        <MenuItem value="__custom__"><em>Valeur personnalisée…</em></MenuItem>
                    </TextField>
                )}
                {showManual && (
                    <TextField
                        size="small"
                        fullWidth
                        type={!isSystemObj
                            ? (inputKind === "datetime" || ui.widget === "datetime" ? "datetime-local" : "date")
                            : "text"}
                        InputLabelProps={{ shrink: true }}
                        label={inputKind === "datetime" ? "Date / heure" : "Date"}
                        value={dialect === "unomi" && !isSystemObj ? toDateInputValue(value) : rawDisplay}
                        onChange={(e) => {
                            let val = e.target.value;
                            if (dialect === "loyalty") {
                                try {
                                    if (val.startsWith("{")) val = JSON.parse(val);
                                } catch {
                                    /* keep string */
                                }
                            }
                            onChange(val);
                        }}
                        helperText={helper || (inputKind === "datetime"
                            ? "Format : AAAA-MM-JJTHH:mm"
                            : "Format : AAAA-MM-JJ")}
                        sx={fsx}
                    />
                )}
            </Box>
        );
    }

    if (
        TYPED_SCALAR_KINDS.has(inputKind)
        && !isTextOperator(operator, dialect)
        && !isListOperator(operator)
        && !isRangeOperator(operator)
    ) {
        return (
            <ValidatedScalarField
                inputKind={inputKind}
                value={value}
                onChange={onChange}
                label={valueKindLabel(inputKind)}
                helperText={helper}
                ui={ui}
            />
        );
    }

    const displayFallback = value !== null && typeof value === "object" && !Array.isArray(value)
        ? JSON.stringify(value)
        : (value ?? "");

    return (
        <TextField
            size="small"
            fullWidth
            label="Valeur"
            value={displayFallback}
            onChange={(e) => {
                let val = e.target.value;
                try {
                    if (val.startsWith("{")) val = JSON.parse(val);
                } catch {
                    /* keep string */
                }
                onChange(val);
            }}
            helperText={helper || (descriptor.isPayload ? "Champ payload — saisissez la valeur attendue par le type d'événement" : undefined)}
            sx={fsx}
        />
    );
}
